import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import {
  APPLICATION_STEPS,
  type ApplicationStep,
  type ApplicationView,
  type BorrowerCaseView,
  type ConsentInput,
  type ConsentPurpose,
  DISCLOSURES,
  type DiscrepancyView,
  type MoneyView,
  REQUIRED_CONSENT_PURPOSES,
} from '@tex/finance-shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BusinessStepForm, EquipmentReviewStepForm, FinancialsStepForm, OwnersStepForm } from '../components/application-steps'
import { Alert, Button, Card, Mono, Row, StageBadge } from '../components/ui'
import { ApiError, errorMessage } from '../lib/api'
import { applicationKeys, useApplication, useSaveApplicationStep, useSubmitApplication } from '../lib/application'
import { type ApplicationDrafts, DISCREPANCY_LABELS, draftsFrom, STEP_BLURBS, STEP_LABELS, toStepPayload, WIZARD_STEPS, type WizardStep } from '../lib/application-form'
import { ENTITY_TYPE_LABELS, FormError } from '../lib/case-form'
import { useCase } from '../lib/cases'
import { formatAmount, formatDate, formatDay } from '../lib/format'
import { useMe } from '../lib/session'

const AUTOSAVE_MS = 1500

/** The step's wire payload, or null while the form is still incomplete. */
function tryPayload<S extends ApplicationStep>(step: S, drafts: ApplicationDrafts) {
  try {
    return toStepPayload(step, drafts)
  } catch (err) {
    if (err instanceof FormError) return null
    throw err
  }
}

export function ApplicationPage({ caseId, step }: { caseId: string; step?: WizardStep }) {
  const me = useMe()
  const kase = useCase(caseId)
  const isBorrower = kase.data?.audience === 'borrower'
  const application = useApplication(caseId, isBorrower)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (kase.isPending || !me.data) return <p className="text-sm text-neutral-500">Loading…</p>
  if (kase.isError) return <Alert tone="error">{errorMessage(kase.error)}</Alert>
  if (kase.data.audience !== 'borrower') {
    return (
      <Alert tone="info">
        The application is the borrower&rsquo;s private part of this request.{' '}
        <Link to="/cases/$caseId" params={{ caseId }} className="underline">
          Back to the request
        </Link>
        .
      </Alert>
    )
  }
  if (application.isPending) return <p className="text-sm text-neutral-500">Loading your application…</p>
  if (application.isError) return <Alert tone="error">{errorMessage(application.error)}</Alert>

  return <Wizard caseId={caseId} view={kase.data} initial={application.data} email={me.data.user.email} requestedStep={step} />
}

function firstOpenStep(view: ApplicationView): WizardStep {
  return APPLICATION_STEPS.find((s) => !view.completed_steps.includes(s)) ?? 'review'
}

function Wizard({
  caseId,
  view,
  initial,
  email,
  requestedStep,
}: {
  caseId: string
  view: BorrowerCaseView
  initial: ApplicationView
  email: string
  requestedStep?: WizardStep
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const live = useApplication(caseId).data ?? initial
  const step: WizardStep = requestedStep ?? firstOpenStep(initial)
  const go = (next: WizardStep) => navigate({ to: '/cases/$caseId/apply', params: { caseId }, search: { step: next } })

  const prefill = useMemo(
    () => ({ legal_name: view.buyer_business_name, entity_type: null, representative_name: view.buyer_contact_name, representative_email: email }),
    [view.buyer_business_name, view.buyer_contact_name, email],
  )
  // Drafts are seeded once from the server; afterwards local edits win until the borrower discards them.
  const [drafts, setDrafts] = useState<ApplicationDrafts>(() => draftsFrom(initial.steps, prefill))
  const [dirty, setDirty] = useState<Set<ApplicationStep>>(() => new Set())
  const [formError, setFormError] = useState<string | null>(null)
  const latestDrafts = useRef(drafts)
  latestDrafts.current = drafts

  const save = useSaveApplicationStep(caseId)
  const { mutateAsync: saveStep } = save
  // After a conflict, nothing is written until the borrower explicitly saves or discards.
  const [autosavePaused, setAutosavePaused] = useState(false)
  const chain = useRef<Promise<unknown>>(Promise.resolve())
  const lastSaved = useRef<Partial<Record<ApplicationStep, string>>>(
    Object.fromEntries(APPLICATION_STEPS.filter((s) => initial.steps[s]).map((s) => [s, JSON.stringify(initial.steps[s])])),
  )

  /**
   * Serialises saves so two in flight never race on `expected_version`; the version is read at
   * send time. A step is only marked clean if what was saved is still what is on screen.
   */
  const persist = useCallback(
    (s: ApplicationStep, current: ApplicationDrafts): Promise<boolean> => {
      const p = chain.current.then(async () => {
        const payload = tryPayload(s, current)
        if (!payload) return false
        const serialised = JSON.stringify(payload)
        if (lastSaved.current[s] === serialised) return true
        const version = qc.getQueryData<ApplicationView>(applicationKeys.detail(caseId))?.version ?? 0
        try {
          await saveStep({ step: s, expected_version: version, payload })
        } catch (err) {
          if (err instanceof ApiError && err.code === 'version_conflict') setAutosavePaused(true)
          throw err
        }
        lastSaved.current[s] = serialised
        if (JSON.stringify(tryPayload(s, latestDrafts.current)) !== serialised) return false
        setDirty((d) => {
          const n = new Set(d)
          n.delete(s)
          return n
        })
        return true
      })
      chain.current = p.catch(() => undefined)
      return p
    },
    [caseId, qc, saveStep],
  )

  const setStep = <S extends ApplicationStep>(s: S, update: (prev: ApplicationDrafts[S]) => ApplicationDrafts[S]) => {
    setDrafts((d) => ({ ...d, [s]: update(d[s]) }))
    setDirty((d) => new Set(d).add(s))
    setFormError(null)
  }

  // Debounced autosave of the step being edited, once it is complete enough to validate.
  useEffect(() => {
    if (step === 'review' || !live.editable || autosavePaused || !dirty.has(step)) return
    const t = setTimeout(() => {
      persist(step, drafts).catch(() => undefined)
    }, AUTOSAVE_MS)
    return () => clearTimeout(t)
  }, [step, drafts, dirty, live.editable, autosavePaused, persist])

  const continueFrom = async (s: ApplicationStep) => {
    setFormError(null)
    try {
      toStepPayload(s, drafts)
    } catch (err) {
      if (err instanceof FormError) {
        setFormError(err.message)
        return
      }
      throw err
    }
    setAutosavePaused(false)
    let saved: boolean
    try {
      saved = await persist(s, drafts)
    } catch {
      return // the mutation error is shown by SaveStatus
    }
    if (!saved) return // edited while saving; the autosave picks the newer value up
    const i = WIZARD_STEPS.indexOf(s)
    go(WIZARD_STEPS[i + 1] ?? 'review')
  }

  const discard = () => {
    setDrafts(draftsFrom(live.steps, prefill))
    setDirty(new Set())
    setAutosavePaused(false)
    lastSaved.current = Object.fromEntries(APPLICATION_STEPS.filter((s) => live.steps[s]).map((s) => [s, JSON.stringify(live.steps[s])]))
    save.reset()
  }

  const stepIndex = WIZARD_STEPS.indexOf(step)
  const stepDirty = step !== 'review' && dirty.has(step)

  return (
    <section className="mx-auto max-w-2xl space-y-5">
      <div>
        <Link to="/cases/$caseId" params={{ caseId }} className="text-sm text-neutral-500 hover:text-neutral-800">
          ← Your financing request
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
          <span style={view.dealer.accent_color ? { color: view.dealer.accent_color } : undefined}>{view.dealer.display_name}</span>
          <StageBadge stage={live.stage} />
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Application for <Mono>{view.reference}</Mono> · {view.buyer_business_name}
        </p>
      </div>

      <Stepper current={step} completed={live.completed_steps} onSelect={go} />

      {!live.editable && live.submitted && (
        <Alert tone="success">
          Submitted {formatDate(live.submitted.submitted_at)} as revision {live.submitted.revision_number}. Tex is reviewing it; your answers below are read-only.
        </Alert>
      )}
      {!live.editable && !live.submitted && <Alert tone="info">This request is no longer taking application changes.</Alert>}

      {step === 'review' ? (
        <ReviewStep caseId={caseId} view={view} live={live} dirty={dirty} onEdit={go} />
      ) : (
        <Card title={`${stepIndex + 1}. ${STEP_LABELS[step]}`}>
          <p className="mb-4 text-sm text-neutral-500">{STEP_BLURBS[step]}</p>
          <StepBody step={step} drafts={drafts} setStep={setStep} view={view} disabled={!live.editable} />
          {formError && (
            <div className="mt-4">
              <Alert tone="error">{formError}</Alert>
            </div>
          )}
          {save.isError && (
            <div className="mt-4">
              <SaveError error={save.error} onDiscard={discard} />
            </div>
          )}
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <SaveStatus pending={save.isPending} dirty={stepDirty} updatedAt={live.updated_at} editable={live.editable} />
            <div className="flex flex-col gap-2 sm:flex-row">
              {stepIndex > 0 && (
                <Button type="button" variant="secondary" onClick={() => go(WIZARD_STEPS[stepIndex - 1] ?? 'business')} disabled={save.isPending}>
                  Back
                </Button>
              )}
              <Button type="button" onClick={() => continueFrom(step)} disabled={save.isPending || !live.editable}>
                {save.isPending ? 'Saving…' : stepIndex === APPLICATION_STEPS.length - 1 ? 'Save and review' : 'Save and continue'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </section>
  )
}

function Stepper({ current, completed, onSelect }: { current: WizardStep; completed: ApplicationStep[]; onSelect: (s: WizardStep) => void }) {
  return (
    <ol className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" aria-label="Application steps">
      {WIZARD_STEPS.map((s, i) => {
        const done = s !== 'review' && completed.includes(s)
        const active = s === current
        return (
          <li key={s} className="shrink-0">
            <button
              type="button"
              onClick={() => onSelect(s)}
              aria-current={active ? 'step' : undefined}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                active ? 'border-tex-blue bg-tex-blue text-white' : done ? 'border-green-200 bg-green-50 text-green-900' : 'border-neutral-300 bg-white text-neutral-700'
              }`}
            >
              {done && !active ? <Check className="h-3 w-3" /> : <span>{i + 1}.</span>}
              {s === 'review' ? 'Review and submit' : STEP_LABELS[s]}
            </button>
          </li>
        )
      })}
    </ol>
  )
}

function StepBody({
  step,
  drafts,
  setStep,
  view,
  disabled,
}: {
  step: ApplicationStep
  drafts: ApplicationDrafts
  setStep: <S extends ApplicationStep>(s: S, update: (prev: ApplicationDrafts[S]) => ApplicationDrafts[S]) => void
  view: BorrowerCaseView
  disabled: boolean
}) {
  switch (step) {
    case 'business':
      return <BusinessStepForm draft={drafts.business} set={(u) => setStep('business', u)} disabled={disabled} />
    case 'owners':
      return <OwnersStepForm owners={drafts.owners} set={(u) => setStep('owners', u)} disabled={disabled} />
    case 'financials':
      return <FinancialsStepForm draft={drafts.financials} set={(u) => setStep('financials', u)} dealerRequested={view.pricing.requested_principal} disabled={disabled} />
    case 'equipment_review':
      return (
        <EquipmentReviewStepForm
          draft={drafts.equipment_review}
          set={(u) => setStep('equipment_review', u)}
          equipment={view.equipment}
          invoiceTotal={view.pricing.invoice_total}
          requested={view.pricing.requested_principal}
          disabled={disabled}
        />
      )
  }
}

function SaveStatus({ pending, dirty, updatedAt, editable }: { pending: boolean; dirty: boolean; updatedAt: string | null; editable: boolean }) {
  if (!editable) return <span className="text-xs text-neutral-500">Read-only</span>
  if (pending) return <span className="text-xs text-neutral-500">Saving…</span>
  if (dirty) return <span className="text-xs text-amber-700">Unsaved changes — saved automatically once the step is complete.</span>
  return <span className="text-xs text-neutral-500">{updatedAt ? `Saved ${formatDate(updatedAt)}. You can leave and come back any time.` : 'Nothing saved yet.'}</span>
}

function SaveError({ error, onDiscard }: { error: unknown; onDiscard: () => void }) {
  if (error instanceof ApiError && error.code === 'version_conflict') {
    return (
      <Alert tone="error">
        This application was saved from another device or tab. Your edits here are kept — press <strong>Save</strong> again to apply them over the latest
        version, or{' '}
        <button type="button" className="underline" onClick={onDiscard}>
          discard your edits and load what was saved
        </button>
        .
      </Alert>
    )
  }
  if (error instanceof ApiError && error.code === 'application_locked') {
    return <Alert tone="error">This application has already been submitted, so it can no longer be changed.</Alert>
  }
  if (error instanceof ApiError && error.code === 'validation_failed') {
    return <Alert tone="error">The server did not accept this step: {error.reason}</Alert>
  }
  return <Alert tone="error">{errorMessage(error)}</Alert>
}

// ── review + submit ──

function fmtValue(v: MoneyView | string | null): string {
  if (v === null) return '—'
  if (typeof v === 'string') return v in ENTITY_TYPE_LABELS ? ENTITY_TYPE_LABELS[v as keyof typeof ENTITY_TYPE_LABELS] : v
  return formatAmount(v)
}

export function DiscrepancyList({ items }: { items: DiscrepancyView[] }) {
  return (
    <ul className="space-y-2 text-sm">
      {items.map((d) => (
        <li key={d.kind} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="font-medium text-amber-900">{DISCREPANCY_LABELS[d.kind]}</div>
          {d.kind === 'quote_disputed' ? (
            <div className="text-amber-900">{d.note}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-amber-900">
              <span>
                Dealer entered: <Mono>{fmtValue(d.dealer_value)}</Mono>
              </span>
              <span>
                You entered: <Mono>{fmtValue(d.borrower_value)}</Mono>
              </span>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

function ReviewStep({
  caseId,
  view,
  live,
  dirty,
  onEdit,
}: {
  caseId: string
  view: BorrowerCaseView
  live: ApplicationView
  dirty: Set<ApplicationStep>
  onEdit: (s: WizardStep) => void
}) {
  const missing = APPLICATION_STEPS.filter((s) => !live.completed_steps.includes(s))
  const unsaved = APPLICATION_STEPS.filter((s) => dirty.has(s))
  const [granted, setGranted] = useState<Set<ConsentPurpose>>(() => new Set(live.submitted?.consents.filter((c) => c.granted).map((c) => c.purpose) ?? []))
  const [confirmed, setConfirmed] = useState(false)
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const submit = useSubmitApplication(caseId, idempotencyKey)

  const requiredGranted = REQUIRED_CONSENT_PURPOSES.every((p) => granted.has(p))
  const canSubmit = live.editable && missing.length === 0 && unsaved.length === 0 && requiredGranted && confirmed && !submit.isPending

  const onSubmit = () => {
    const consents: ConsentInput[] = [...granted].map((p) => ({ purpose: p, disclosure_version: DISCLOSURES[p].version, granted: true }))
    submit.mutate({ expected_version: live.version, consents })
  }

  const s = live.steps
  return (
    <div className="space-y-4">
      {(missing.length > 0 || unsaved.length > 0) && live.editable && (
        <Alert tone="info">
          {missing.length > 0 && (
            <>
              Not finished yet: {missing.map((m) => STEP_LABELS[m]).join(', ')}.{' '}
            </>
          )}
          {unsaved.length > 0 && <>Unsaved edits on: {unsaved.map((m) => STEP_LABELS[m]).join(', ')} — go back and press Save.</>}
        </Alert>
      )}

      <Card title="Your business" actions={live.editable && <EditLink onClick={() => onEdit('business')} />}>
        {s.business ? (
          <dl>
            <Row label="Legal name" value={s.business.legal_name} />
            <Row label="Trade name" value={s.business.trading_name} />
            <Row label="Entity type" value={ENTITY_TYPE_LABELS[s.business.entity_type]} />
            <Row label="Formed" value={formatDay(s.business.formed_on)} />
            <Row label="Industry" value={s.business.industry} />
            <Row label="Address" value={`${s.business.business_address.city}, ${s.business.business_address.state}`} />
            <Row label="Applicant" value={`${s.business.representative.name}, ${s.business.representative.title}`} />
          </dl>
        ) : (
          <Empty />
        )}
      </Card>

      <Card title="Owners" actions={live.editable && <EditLink onClick={() => onEdit('owners')} />}>
        {s.owners ? (
          <ul className="space-y-1 text-sm">
            {s.owners.owners.map((o) => (
              <li key={o.email} className="flex justify-between gap-2">
                <span>
                  {o.full_name} <span className="text-neutral-500">· {o.title}</span>
                </span>
                <Mono>{o.ownership_percent}%</Mono>
              </li>
            ))}
          </ul>
        ) : (
          <Empty />
        )}
      </Card>

      <Card title="Financing request" actions={live.editable && <EditLink onClick={() => onEdit('financials')} />}>
        {s.financials ? (
          <dl>
            <Row label="Amount requested" value={<Mono className="font-semibold">{formatAmount(s.financials.requested_amount)}</Mono>} />
            <Row label="For" value={s.financials.intended_use} />
            <Row label="Repaid from" value={s.financials.repayment_source} />
            <Row label="Annual revenue" value={s.financials.annual_revenue.status === 'stated' ? <Mono>{formatAmount(s.financials.annual_revenue.amount)}</Mono> : 'Not known'} />
            <Row
              label="Monthly debt payments"
              value={s.financials.monthly_debt_service.status === 'stated' ? <Mono>{formatAmount(s.financials.monthly_debt_service.amount)}</Mono> : 'Not known'}
            />
          </dl>
        ) : (
          <Empty />
        )}
      </Card>

      <Card title="Equipment" actions={live.editable && <EditLink onClick={() => onEdit('equipment_review')} />}>
        {s.equipment_review ? (
          <p className="text-sm">
            {s.equipment_review.quote_matches ? 'You confirmed the equipment and price match what you agreed.' : `You flagged a difference: ${s.equipment_review.note ?? 'see item notes'}.`}{' '}
            <span className="text-neutral-500">Dealer&rsquo;s invoice total {formatAmount(view.pricing.invoice_total)}.</span>
          </p>
        ) : (
          <Empty />
        )}
      </Card>

      {live.discrepancies.length > 0 && (
        <Card title="Differences from the dealer's request">
          <p className="mb-3 text-sm text-neutral-500">
            These are not errors. Both versions are kept and Tex reconciles them with you and the dealer before anything goes to a financing partner. Change your
            answers only if you made a mistake.
          </p>
          <DiscrepancyList items={live.discrepancies} />
        </Card>
      )}

      {live.submitted ? (
        <Card title="Consents you gave">
          <ul className="space-y-1 text-sm">
            {live.submitted.consents.map((c) => (
              <li key={c.purpose} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-700" />
                {DISCLOSURES[c.purpose].title} <span className="text-neutral-500">· {formatDate(c.recorded_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card title="Consents">
          <div className="space-y-3">
            {(Object.keys(DISCLOSURES) as ConsentPurpose[]).map((p) => {
              const d = DISCLOSURES[p]
              const isRequired = REQUIRED_CONSENT_PURPOSES.includes(p)
              return (
                <label key={p} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={granted.has(p)}
                    disabled={!live.editable || submit.isPending}
                    onChange={(e) =>
                      setGranted((g) => {
                        const n = new Set(g)
                        if (e.target.checked) n.add(p)
                        else n.delete(p)
                        return n
                      })
                    }
                  />
                  <span>
                    <span className="font-medium">
                      {d.title}
                      {isRequired ? '' : ' (optional — can be given later)'}
                    </span>
                    <span className="block text-neutral-500">{d.text}</span>
                    <span className="block text-xs text-neutral-400">Version {d.version}</span>
                  </span>
                </label>
              )
            })}
          </div>

          <div className="mt-5 space-y-3 border-t border-neutral-100 pt-4">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!live.editable || submit.isPending} />
              <span>I have checked my answers. Once submitted they are frozen as a revision; any correction becomes a new revision.</span>
            </label>
            {submit.isError && <SubmitError error={submit.error} />}
            <Button type="button" onClick={onSubmit} disabled={!canSubmit} className="w-full justify-center sm:w-auto">
              {submit.isPending ? 'Submitting…' : 'Submit application'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

function EditLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="text-xs text-tex-blue hover:underline">
      Edit
    </button>
  )
}

function Empty() {
  return <p className="text-sm text-neutral-500">Not filled in yet.</p>
}

function SubmitError({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    if (error.code === 'version_conflict') return <Alert tone="error">Your draft changed since this page loaded; it has been reloaded — check your answers and submit again.</Alert>
    if (error.code === 'application_incomplete') return <Alert tone="error">Some steps are not finished: {error.reason}.</Alert>
    if (error.code === 'disclosure_version_stale') return <Alert tone="error">The consent text has been updated since this page loaded. Reload the page and review the new text.</Alert>
    if (error.code === 'application_locked') return <Alert tone="error">This application has already been submitted.</Alert>
  }
  return <Alert tone="error">{errorMessage(error)}</Alert>
}
